import type { MouseEventHandler } from 'react';
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Headline, Icon } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import type { Category } from '../../types/audi-footer-response.types';
import Links from '../links/links';
import { breakpointM } from '../footer/audi-footer';
import { breakpoints } from '../../utils/breakpoints';
import { responsiveStyles } from '../../utils/responsiveStyles';
import {
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_350,
  SYS_SPACE_FIXED_500,
} from '@oneaudi/unified-web-common';

export type CategoriesProps = {
  categories?: Category[];
  useOneLayer?: boolean;
  openLayer: MouseEventHandler;
};

export const timeL = '240ms';

const Categories: React.FC<CategoriesProps> = ({ categories, useOneLayer, openLayer }) => {
  const [openStates, setOpenStates] = useState(categories?.map(() => false) || []);
  const buttonsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < breakpointM);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const toggleSubmenu = (index: number) => {
    lastFocusedElementRef.current = document.activeElement as HTMLElement;
    setOpenStates((prev) => prev.map((state, i) => (i === index ? !state : state)));
  };

  useLayoutEffect(() => {
    if (lastFocusedElementRef.current) {
      lastFocusedElementRef.current.focus();
      lastFocusedElementRef.current = null;
    }
  }, [openStates]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isMobile) toggleSubmenu(index);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttonsRef.current[(index + 1) % buttonsRef.current.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttonsRef.current[
        (index - 1 + buttonsRef.current.length) % buttonsRef.current.length
      ]?.focus();
    } else if (event.key === 'Tab') {
      const isOpen = openStates[index];
      if (!isOpen) {
        const submenu = document.getElementById(`submenu-${index}`);
        if (submenu) {
          const focusables = submenu.querySelectorAll(
            'a, button, input, [tabindex]:not([tabindex="-1"])',
          );
          focusables.forEach((el) => {
            el.setAttribute('tabIndex', '-1');
          });
        }
      }
    }
  };

  return (
    <FooterSitemapStyled>
      <ul>
        {categories?.map((category, i) => {
          const { Link, SubLinks } = category;
          const hasSubLinks = !!SubLinks?.length;
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const key = `${useId()}-${Link.Text}`;
          return (
            <li key={key}>
              <FooterSitemapCategoryLinkWrapperStyled
                id={`button-${i}`}
                ref={(el) => {
                  buttonsRef.current[i] = el;
                }}
                {...(isMobile && hasSubLinks
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      onClick: () => {
                        toggleSubmenu(i);
                      },
                      onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                        handleKeyDown(e, i);
                      },
                      'aria-controls': `submenu-${i}`,
                      'aria-expanded': openStates[i],
                      'data-submenu-open': openStates[i],
                    }
                  : {})}
              >
                <Headline
                  className="category_label"
                  tag="h4"
                  variant={{
                    order: '4',
                    type: 'headline',
                    weight: 'normal',
                  }}
                >
                  {Link.Text}
                  {hasSubLinks ? <Icon name="arrow-down" size="s" /> : null}
                </Headline>
              </FooterSitemapCategoryLinkWrapperStyled>

              {hasSubLinks && (
                <Links
                  id={`submenu-${i}`}
                  categoryIndex={i.toString()}
                  headline={Link.Text}
                  links={SubLinks}
                  useOneLayer={useOneLayer}
                  openLayer={openLayer}
                  isOpen={openStates[i]}
                />
              )}
            </li>
          );
        })}
      </ul>
    </FooterSitemapStyled>
  );
};
const FooterSitemapStyled = styled.div`
  & > ul {
    display: block;
    list-style: none;
    margin: 0 0 var(--one-footer-space-xxl) calc(-1 * var(--one-footer-side-spacing));
    /* stylelint-disable */
    ${(): string =>
      responsiveStyles(
        {
          display: { m: 'flex' },
          'flex-flow': { m: 'row wrap' },
          margin: { m: `${SYS_SPACE_FIXED_350} auto ${SYS_SPACE_FIXED_350}` },
          width: { m: '100%' },
        },
        breakpoints,
      )}
    /* stylelint-enable */
    padding: 0;
    width: calc(100% + 2 * var(--one-footer-side-spacing));

    & > li {
      margin-bottom: -1px;

      ${(): string =>
        responsiveStyles(
          {
            margin: {
              m: `0 ${SYS_SPACE_FIXED_300} ${SYS_SPACE_FIXED_500}  0`,
              xl: `0 ${SYS_SPACE_FIXED_300} ${SYS_SPACE_FIXED_500}  0`,
            },
            padding: { m: '0' },
            width: {
              m: 'calc(33% - var(--one-footer-space-xl) * 2 / 3)',
              xl: 'calc(20% - var(--one-footer-space-xl) * 4 / 5)',
            },
          },
          breakpoints,
        )}

      &:nth-of-type(3n) {
        ${(): string =>
          responsiveStyles(
            {
              'margin-right': {
                m: '0',
                xl: 'var(--one-footer-space-l)',
              },
            },
            breakpoints,
          )}
      }

      &:nth-of-type(5n) {
        ${(): string =>
          responsiveStyles(
            {
              'margin-right': {
                xl: '0',
              },
            },
            breakpoints,
          )}
      }

      &:first-of-type {
        /* stylelint-disable */
        margin-top: calc(var(--one-footer-space-xxxl) - var(--one-footer-space-l));
        /* stylelint-enable */
        ${(): string =>
          responsiveStyles(
            {
              'margin-top': { m: '0' },
            },
            breakpoints,
          )}
      }

      &:last-of-type {
        /* stylelint-disable */
        margin-bottom: calc(var(--one-footer-space-xxl) - var(--one-footer-space-l));
        /* stylelint-enable */
        ${(): string =>
          responsiveStyles(
            {
              'margin-bottom': { m: 'var(--one-footer-space-xxl)' },
            },
            breakpoints,
          )}
      }
    }
  }
`;

const FooterSitemapCategoryLinkWrapperStyled = styled.div`
  align-content: center;
  border-bottom: 1px solid var(--one-footer-neutral-10);
  border-top: 1px solid var(--one-footer-neutral-10);
  cursor: pointer;
  padding: 0 var(--one-footer-side-spacing);
  position: relative;
  ${(): string =>
    responsiveStyles(
      {
        border: { m: 'none' },
        cursor: { m: 'initial' },
        'margin-bottom': { m: SYS_SPACE_FIXED_300 },
        padding: { m: '0' },
      },
      breakpoints,
    )}
  width: 100%;
  z-index: 2;

  & > .category_label {
    color: var(--one-footer-color-white);
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    padding: var(--one-footer-space-m) 0;
    ${(): string =>
      responsiveStyles(
        {
          padding: { m: '0' },
          width: { m: 'auto' },
        },
        breakpoints,
      )}
    width: 100%;

    & > svg {
      flex: none; /* prevent arrow shrink on long labels */
      margin: 0 0 0 var(--one-footer-space-l);
      transition: transform ${timeL} cubic-bezier(0.75, 0.02, 0.5, 1);
      ${(): string =>
        responsiveStyles(
          {
            display: { m: 'none' },
          },
          breakpoints,
        )}
    }
  }

  &[data-submenu-open*='true'] svg {
    transform: rotate(-180deg);
  }

  &[data-submenu-open*='true'] + div {
    background-color: var(--one-footer-neutral-5);
    height: auto;
    max-height: 2000px;
    opacity: 1;
    ${(): string =>
      responsiveStyles(
        {
          'background-color': { m: 'var(--one-footer-color-black)' },
        },
        breakpoints,
      )}
  }
`;

export default Categories;
